import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material'
import React, { useEffect, useState } from 'react'

import Button from '@/components/Button'
import type { ImageLabel } from '@/utils/constants'

interface AddCustomLabelDialogProps {
  open: boolean
  onClose: () => void
  onAdd: (label: { name: string; description?: string }) => void
  onUpdate?: (label: { name: string; description?: string }) => void
  isLoading?: boolean
  mode?: 'add' | 'edit'
  initialLabel?: ImageLabel | null
}

export default function AddCustomLabelDialog({
  open,
  onClose,
  onAdd,
  onUpdate,
  isLoading = false,
  mode = 'add',
  initialLabel = null,
}: AddCustomLabelDialogProps) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')

  // Pre-populate fields when opening in edit mode
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && initialLabel) {
        setName(initialLabel.name)
        setDescription(initialLabel.description || '')
      } else {
        setName('')
        setDescription('')
      }
    }
  }, [open, mode, initialLabel])

  const handleClose = () => {
    setName('')
    setDescription('')
    onClose()
  }

  const handleSubmit = () => {
    if (!name.trim()) return
    const payload = {
      name: name.trim(),
      description: description.trim() || undefined,
    }
    if (mode === 'edit') {
      onUpdate?.(payload)
    } else {
      onAdd(payload)
      setName('')
      setDescription('')
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && name.trim()) {
      handleSubmit()
    }
  }

  const isEdit = mode === 'edit'

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            bgcolor: '#2a2a2a',
            borderRadius: '8px',
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            marginTop: '5.625rem',
            height: 'auto',
            maxHeight: 'calc(100% - 130px)',
          },
        },
      }}
    >
      <DialogTitle sx={{ py: 2, px: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography fontWeight={400} fontSize="1.25rem" color="white">
            {isEdit ? 'Update Custom Label' : 'Add Custom Label'}
          </Typography>
          <IconButton
            onClick={handleClose}
            aria-label={
              isEdit
                ? 'close edit custom label dialog'
                : 'close add custom label dialog'
            }
            sx={{ p: 0, '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' } }}
          >
            <CloseIcon sx={{ color: 'white' }} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ py: 2, px: 3, paddingTop: '12px !important' }}>
        <TextField
          id="custom-label-name-input"
          label="Custom Label Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={handleKeyDown}
          fullWidth
          variant="outlined"
          size="small"
          inputProps={{ maxLength: 100 }}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              bgcolor: '#1e1e1e',
              borderRadius: '4px',
              '& fieldset': { borderColor: '#555' },
              '&:hover fieldset': { borderColor: '#888' },
              '&.Mui-focused fieldset': { borderColor: '#90caf9' },
            },
            '& .MuiInputLabel-root': { color: '#888', fontSize: '13px' },
            '& .MuiInputLabel-root.Mui-focused': { color: '#90caf9' },
            '& input': {
              color: 'white',
              fontSize: '14px',
              padding: '14px 14px',
            },
          }}
        />
        <TextField
          id="custom-label-description-input"
          label="Description (Optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          variant="outlined"
          multiline
          rows={5}
          inputProps={{ maxLength: 500 }}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: '#1e1e1e',
              borderRadius: '4px',
              alignItems: 'flex-start',
              '& fieldset': { borderColor: '#555' },
              '&:hover fieldset': { borderColor: '#888' },
              '&.Mui-focused fieldset': { borderColor: '#90caf9' },
            },
            '& .MuiInputLabel-root': { color: '#888', fontSize: '13px' },
            '& .MuiInputLabel-root.Mui-focused': { color: '#90caf9' },
            '& textarea': { color: 'white', fontSize: '14px' },
          }}
        />
      </DialogContent>

      <DialogActions sx={{ py: 2, px: 3 }}>
        <Box display="flex" justifyContent="flex-end" gap={2} width="100%">
          <Button
            id="custom-label-cancel-btn"
            label="CANCEL"
            variant="outlined"
            textTransform="uppercase"
            onClick={handleClose}
          />
          <Button
            id={isEdit ? 'custom-label-update-btn' : 'custom-label-add-btn'}
            label={
              isLoading
                ? isEdit
                  ? 'UPDATING...'
                  : 'ADDING...'
                : isEdit
                  ? 'UPDATE'
                  : 'ADD'
            }
            variant="contained"
            color="primary"
            textTransform="uppercase"
            disabled={!name.trim() || isLoading}
            onClick={handleSubmit}
          />
        </Box>
      </DialogActions>
    </Dialog>
  )
}
